/**
 * `/welcome` — where a client lands the second their card is charged, and where
 * they keep coming back until the site is live.
 *
 * The model is a delivery order: one headline that says what's happening right
 * now, a tracker rail, an ETA, a short list of what's ours and what's theirs,
 * and a way to message the people doing it. It is deliberately the whole screen
 * — not a banner on a half-empty dashboard.
 *
 * Data comes from `useSetupStatus`. `?demo=1` runs it with no backend at all
 * (auto-advancing through every stage); `?stage=review` parks it on one.
 */

import React, { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { useSetupStatus } from '../../hooks/useSetupStatus';
import {
  SETUP_STAGES,
  STAGE_DEFS,
  stageIndex,
  type SetupStage,
  type SetupTask,
} from '../../lib/setupFlow';
import { PLAN_NAMES } from '../../lib/pricing';
import { Eyebrow, H1, H3, Mono, Panel, PanelBody, PanelHeader, Rule, Tag } from '../ui/kit';
import { cn } from '@/lib/utils';
import StageTracker from './StageTracker';
import TaskRouter from './TaskRouter';
import DomainCallout from './DomainCallout';
import MessageTeamPanel from './MessageTeamPanel';
import { clearTaskStore, isInFlowTask, readTaskStore, saveTaskState } from '../../lib/setupTasks';
import { personalFirstName } from '../../lib/contactName';


/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.round(hours / 24)} d ago`;
}

function formatEta(iso: string | null): { day: string; window: string } | null {
  if (!iso) return null;
  const date = new Date(iso);
  const today = new Date();
  const isTomorrow = date.toDateString() === new Date(today.getTime() + 86_400_000).toDateString();

  return {
    day: date.toDateString() === today.toDateString()
      ? 'Today'
      : isTomorrow
        ? 'Tomorrow'
        : date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' }),
    window: date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
  };
}

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */

function LivePulse({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="relative flex h-2 w-2">
        <motion.span
          className="absolute inline-flex h-full w-full rounded-full bg-accent-light"
          animate={{ scale: [1, 2.2, 1], opacity: [0.7, 0, 0.7] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
        />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
      </span>
      <span className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">{label}</span>
    </span>
  );
}

function StepTick({ label, done, active }: { label: string; done: boolean; active: boolean }) {
  return (
    <li className="flex items-start gap-3 py-2.5">
      <span
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
          done ? 'border-primary bg-primary text-white' : active ? 'border-primary' : 'border-zinc-300'
        )}
      >
        {done ? (
          <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth={4}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
          </svg>
        ) : active ? (
          <motion.span
            className="h-1.5 w-1.5 rounded-full bg-primary"
            animate={{ opacity: [1, 0.25, 1] }}
            transition={{ duration: 1.6, repeat: Infinity }}
          />
        ) : null}
      </span>
      <span className={cn('text-sm leading-snug', done ? 'text-zinc-500' : active ? 'text-primary' : 'text-zinc-400')}>
        {label}
      </span>
    </li>
  );
}

function TaskRow({ task, onOpen }: { task: SetupTask; onOpen?: (taskId: string) => void }) {
  const isDone = task.status === 'done';
  const opensInFlow = Boolean(onOpen) && isInFlowTask(task.id);

  return (
    <div className={cn('flex flex-wrap items-start gap-4 px-5 py-4', isDone && 'opacity-60')}>
      <span
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
          isDone ? 'border-primary bg-primary text-white' : 'border-zinc-300'
        )}
      >
        {isDone && (
          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
          </svg>
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('text-sm font-semibold text-primary', isDone && 'line-through')}>{task.title}</span>
          {task.owner === 'foundlio' && !isDone && <Tag tone="neutral">We handle this</Tag>}
        </div>
        <p className="mt-1 text-sm leading-snug text-muted">{task.description}</p>
      </div>

      {!isDone && task.owner === 'you' && task.cta && (
        // In-flow steps stay inside /welcome — sending someone into a settings
        // screen mid-setup is how they fall out of the flow. Anything without a
        // panel falls back to its dashboard route.
        opensInFlow ? (
          <button
            type="button"
            onClick={() => onOpen?.(task.id)}
            className="shrink-0 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            {task.cta}
          </button>
        ) : task.href?.startsWith('/') ? (
          <Link
            to={task.href}
            className="shrink-0 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            {task.cta}
          </Link>
        ) : task.href ? (
          <a
            href={task.href}
            className="shrink-0 rounded-lg border border-zinc-300 px-3.5 py-2 text-sm font-semibold text-primary transition-colors hover:border-primary"
          >
            {task.cta}
          </a>
        ) : null
      )}
    </div>
  );
}

/** Demo-only strip so the whole flow can be walked without a backend. */
function DemoControls({ stage, onPick }: { stage: SetupStage; onPick: (stage: SetupStage) => void }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t border-zinc-200 bg-white/95 px-4 py-2.5 backdrop-blur">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-400">Demo</span>
        {SETUP_STAGES.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => onPick(id)}
            className={cn(
              'rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-eyebrow transition-colors',
              stage === id ? 'border-primary bg-primary text-white' : 'border-zinc-300 text-zinc-500 hover:border-primary'
            )}
          >
            {STAGE_DEFS[id].label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => clearTaskStore(true)}
          className="rounded-full border border-zinc-300 px-3 py-1 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500 transition-colors hover:border-primary"
        >
          Reset steps
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Screen                                                              */
/* ------------------------------------------------------------------ */

export default function WelcomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  const demo = searchParams.get('demo') === '1';
  const stageParam = searchParams.get('stage');
  const stageOverride = SETUP_STAGES.includes(stageParam as SetupStage) ? (stageParam as SetupStage) : null;
  const openTaskId = searchParams.get('task');

  // The open panel is a URL param, not component state: back button works, the
  // client can be sent a link straight to the step that is blocking us, and a
  // reload lands them where they were.
  const openTask = (taskId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('task', taskId);
    setSearchParams(next);
    window.scrollTo({ top: 0 });
  };

  const closeTask = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('task');
    setSearchParams(next);
    window.scrollTo({ top: 0 });
  };

  const { status, isLoading, isError, updatedAt, refresh, setDemoStage } = useSetupStatus({
    demo,
    stageOverride,
    enabled: !authLoading && isAuthenticated,
  });

  // Keeps "updated 12s ago" honest without re-fetching.
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setTick((n) => n + 1), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  // Real setup belongs to a signed-in client. Demo mode is the one exception.
  if (!demo && !authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (authLoading || isLoading || (!status && !isError)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-zinc-300 border-t-primary" />
      </div>
    );
  }

  if (!status) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-6">
        <div className="max-w-sm text-center">
          <Eyebrow className="mb-3">Setup</Eyebrow>
          <H1>We couldn&apos;t load your setup.</H1>
          <p className="mt-3 text-sm text-muted">
            Your account is fine — this screen just couldn&apos;t reach the server.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              type="button"
              onClick={refresh}
              className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white"
            >
              Try again
            </button>
            <Link
              to="/dashboard"
              className="rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-primary"
            >
              Go to dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const def = STAGE_DEFS[status.stage];
  // "Live" is live on the client's own domain — a site answering only on a
  // Foundlio address is still a preview, and this screen must not say otherwise.
  const isLive = status.stage === 'live';
  const liveDomain = status.customDomain ?? status.domain;
  const eta = formatEta(status.etaAt);
  // Only what they can act on *now*. Tasks still upstream of the current stage
  // stay out of the way — a checklist that lists things you can't do yet is
  // just anxiety.
  const yourTasks = status.tasks.filter((task) => task.owner === 'you' && task.status === 'todo');
  // The domain has a callout of its own at the top of the column — repeating it
  // as a row three inches below is the same button twice.
  const listedTasks = yourTasks.filter((task) => task.id !== 'domain');
  const doneCount = status.tasks.filter((task) => task.status === 'done').length;
  // `user.name` holds the business name on pipeline-created accounts, so this
  // has to be gated — see the same guard on the dashboard and the invite page.
  const firstName = personalFirstName(user?.name);

  const header = (
    <header className="border-b border-zinc-200 bg-paper-tint">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="font-display text-base font-bold tracking-tight text-primary">Foundlio</span>
          <span className="hidden h-4 w-px bg-zinc-300 sm:block" />
          <Mono className="hidden sm:block">{status.orderRef}</Mono>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline-flex">
            <LivePulse label={`Updated ${relativeTime(new Date(updatedAt || Date.now()).toISOString())}`} />
          </span>
          <Link
            to="/dashboard"
            className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500 transition-colors hover:text-primary"
          >
            Dashboard →
          </Link>
        </div>
      </div>
    </header>
  );

  // A task panel takes over the whole screen: one thing to do, nothing else to
  // look at. The tracker is one back-link away.
  if (openTaskId) {
    return (
      <div className={cn('min-h-screen bg-paper', demo && 'pb-16')}>
        {header}
        <main className="mx-auto max-w-5xl px-5 py-10 sm:py-14">
          <TaskRouter
            taskId={openTaskId}
            status={status}
            record={readTaskStore(demo)[openTaskId]}
            editorHref={
              status.websiteId ? `/dashboard/websites/${status.websiteId}/edit` : '/dashboard/websites'
            }
            onBack={closeTask}
            onComplete={(data) => {
              saveTaskState(openTaskId, { completedAt: new Date().toISOString(), data }, demo);
            }}
          />
        </main>
        {demo && <DemoControls stage={status.stage} onPick={setDemoStage} />}
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen bg-paper', demo && 'pb-16')}>
      {header}

      <main className="mx-auto max-w-5xl px-5 py-10 sm:py-14">
        {/* Hero. Keyed remount rather than AnimatePresence: a stage can change
            twice in quick succession, and a queued exit must never be able to
            leave last stage's headline on screen. */}
        <motion.div
          key={status.stage}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <Eyebrow className="mb-3">
            {status.businessName}
            {firstName ? ` · ${firstName}` : ''}
          </Eyebrow>
          <H1 className="max-w-2xl sm:text-[2.5rem]">{def.headline}</H1>
          <Rule className={cn('mt-5', isLive && 'bg-accent')} />
          <p className="mt-5 max-w-xl text-base leading-relaxed text-muted">{def.detail}</p>
        </motion.div>

        {/* ETA — the "arriving by" line */}
        <div className="mt-8 flex flex-wrap items-end gap-x-10 gap-y-4">
          {eta ? (
            <div>
              <Eyebrow className="mb-2">Estimated live</Eyebrow>
              <div className="font-display text-[1.75rem] font-bold leading-none tracking-[-0.03em] text-primary">
                {eta.day}
              </div>
              <div className="mt-1.5 font-mono text-[12px] text-zinc-500">around {eta.window}</div>
            </div>
          ) : (
            <div>
              <Eyebrow className="mb-2">Status</Eyebrow>
              <div className="font-display text-[1.75rem] font-bold leading-none tracking-[-0.03em] text-accent">
                Live now
              </div>
              <div className="mt-1.5 font-mono text-[12px] text-zinc-500">{liveDomain}</div>
            </div>
          )}

          <div>
            <Eyebrow className="mb-2">Stage</Eyebrow>
            <div className="font-display text-[1.75rem] font-bold leading-none tracking-[-0.03em] text-primary">
              {stageIndex(status.stage) + 1}
              <span className="text-zinc-400">/{SETUP_STAGES.length}</span>
            </div>
            <div className="mt-1.5 font-mono text-[12px] text-zinc-500">
              started {relativeTime(status.stageStartedAt)}
            </div>
          </div>

          <div>
            <Eyebrow className="mb-2">Checklist</Eyebrow>
            <div className="font-display text-[1.75rem] font-bold leading-none tracking-[-0.03em] text-primary">
              {doneCount}
              <span className="text-zinc-400">/{status.tasks.length}</span>
            </div>
            <div className="mt-1.5 font-mono text-[12px] text-zinc-500">
              {yourTasks.length ? `${yourTasks.length} waiting on you` : 'nothing on you'}
            </div>
          </div>
        </div>

        {/* Tracker */}
        <div className="mt-8">
          <StageTracker stage={status.stage} />
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.35fr_1fr]">
          {/* Left column */}
          <div className="space-y-6">
            {/* The domain — the step that actually ends setup, so it leads. */}
            <DomainCallout
              status={status}
              isLastStep={listedTasks.length === 0}
              onOpen={() => openTask('domain')}
            />

            {/* What's happening now */}
            <Panel>
              <PanelHeader
                title={isLive ? 'Everything that shipped' : 'Happening now'}
                actions={!isLive ? <LivePulse label="In progress" /> : undefined}
              />
              <PanelBody className="py-2">
                <ul>
                  {def.steps.map((step, index) => (
                    <StepTick
                      key={step}
                      label={step}
                      done={index < status.completedSteps}
                      active={index === status.completedSteps}
                    />
                  ))}
                </ul>
              </PanelBody>
            </Panel>

            {/* Waiting on you */}
            {listedTasks.length > 0 && (
              <Panel className="border-primary/25">
                <PanelHeader title={`Waiting on you · ${listedTasks.length}`} />
                <div className="divide-y divide-zinc-200">
                  {listedTasks.map((task) => (
                    <TaskRow key={task.id} task={task} onOpen={openTask} />
                  ))}
                </div>
              </Panel>
            )}

            {/* Timeline */}
            {status.events.length > 0 && (
              <Panel>
                <PanelHeader title="Activity" />
                <PanelBody>
                  <ol className="space-y-0">
                    {status.events.map((event, index) => (
                      <li key={event.id} className="relative flex gap-4 pb-5 last:pb-0">
                        {index !== status.events.length - 1 && (
                          <span className="absolute left-[5px] top-3 h-full w-px bg-zinc-200" aria-hidden />
                        )}
                        <span
                          className={cn(
                            'relative mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full',
                            index === 0 ? 'bg-primary' : 'bg-zinc-300'
                          )}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                            <span className="text-sm font-semibold text-primary">{event.title}</span>
                            <Mono className="text-[11px] text-zinc-400">{relativeTime(event.at)}</Mono>
                          </div>
                          {event.detail && <p className="mt-0.5 text-sm text-muted">{event.detail}</p>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </PanelBody>
              </Panel>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* Preview / live site */}
            <Panel id="preview" className="overflow-hidden">
              <PanelHeader title={isLive ? 'Your website' : 'Your draft'} />
              {status.screenshotUrl ? (
                <img
                  src={status.screenshotUrl}
                  alt={`${status.businessName} website`}
                  className="h-44 w-full border-b border-zinc-200 object-cover object-top"
                />
              ) : (
                <div className="flex h-32 items-center justify-center border-b border-zinc-200 bg-paper-deep">
                  <Mono className="text-zinc-400">
                    {status.previewUrl ? 'preview ready' : 'preview appears at review stage'}
                  </Mono>
                </div>
              )}
              <PanelBody className="space-y-3">
                <div>
                  <Eyebrow className="mb-1.5">{isLive ? 'Domain' : 'Preview link'}</Eyebrow>
                  <Mono className="break-all">
                    {isLive ? liveDomain : status.previewUrl ?? 'Not ready yet'}
                  </Mono>
                </div>
                {(status.previewUrl || isLive) && (
                  <a
                    href={isLive && liveDomain ? `https://${liveDomain}` : status.previewUrl ?? '#'}
                    target="_blank"
                    rel="noreferrer"
                    className={cn(
                      'block rounded-lg px-4 py-2.5 text-center text-sm font-semibold transition-opacity hover:opacity-90',
                      isLive ? 'bg-accent text-white' : 'bg-primary text-white'
                    )}
                  >
                    {isLive ? 'Open my website' : 'Open the draft'}
                  </a>
                )}
              </PanelBody>
            </Panel>

            {/* Order summary */}
            <Panel>
              <PanelHeader title="Your plan" />
              <PanelBody className="space-y-3.5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-muted">Plan</span>
                  <span className="text-sm font-semibold text-primary">
                    {PLAN_NAMES[status.plan]} · ${status.monthly}/mo
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-muted">Setup fee</span>
                  <span className="text-sm font-semibold text-primary">$0</span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-muted">Business</span>
                  <span className="truncate text-sm font-semibold text-primary">{status.businessName}</span>
                </div>
                <div className="border-t border-zinc-200 pt-3">
                  <Link
                    to="/dashboard/account/billing"
                    className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500 transition-colors hover:text-primary"
                  >
                    Billing &amp; invoices →
                  </Link>
                </div>
              </PanelBody>
            </Panel>

            <MessageTeamPanel status={status} />

            {isLive && (
              <Panel className="border-accent/30">
                <PanelBody className="text-center">
                  <H3>Setup is done.</H3>
                  <p className="mt-2 text-sm leading-snug text-muted">
                    Leads, calls and reviews all land in your dashboard from here.
                  </p>
                  <Link
                    to="/dashboard"
                    className="mt-4 block rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    Go to my dashboard
                  </Link>
                </PanelBody>
              </Panel>
            )}
          </div>
        </div>

        {status.isMock && (
          <p className="mt-10 text-center font-mono text-[11px] uppercase tracking-eyebrow text-zinc-400">
            Demo data · no backend calls
          </p>
        )}
      </main>

      {demo && <DemoControls stage={status.stage} onPick={setDemoStage} />}
    </div>
  );
}
